import {
  PlainButton,
  Popover,
  Tooltip,
  SearchInput,
  Radio,
  ListBox,
  ListBoxItem,
} from "@jugl-web/ui-kit";
import {
  cx,
  useAppVariant,
  useDebounce,
  useSearchInput,
  useTranslations,
} from "@jugl-web/utils";
import { Screen } from "@jugl-web/utils/utils/ScreenTransitionManager";
import React, { useEffect, useMemo, useState } from "react";
import {
  CRMChannel,
  CRMConversationFilter,
  CRMConversationMode,
} from "@jugl-web/rest-api/integrations/types";
import { MessageCreditsDialog } from "@web-src/modules/cx/components/CXCreditsTile/components/MessageCreditsDialog";
import { useAIMessageCredits } from "@web-src/modules/cx/hooks/useAIMessageCredits";
import { collapsedIconButtonClassName } from "../../consts";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import { ReactComponent as FilterIcon } from "./assets/filter.svg";
import { ReactComponent as SupportIcon } from "./assets/support.svg";
import { ReactComponent as AllChatsIcon } from "./assets/all-chats.svg";
import { ReactComponent as AIIcon } from "./assets/ai.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as InsightsIcon } from "./assets/insights.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as WarningIcon } from "./assets/warning.svg";
import { ConversationTab } from "./components/ConversationTab";
import { useClientSupportPageContext } from "../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { WhatsappConversations } from "./components/WhatsappConversations";
import { InstagramConversations } from "./components/InstagramConversations";
import { WebChatBotConversations } from "./components/WebChatBotConversations";
import { TwilioConversations } from "./components/TwilioConversations";
import { VoiceConversations } from "./components/VoiceConversations";
import { EmailConversations } from "./components/EmailConversations";
import { useManageAccountsDrawer } from "../ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { ManageAccountsDrawerScreenToParametersMap } from "../ManageAccountsDrawer/types";

const channelToAccountScreen: Record<
  CRMChannel,
  Screen<ManageAccountsDrawerScreenToParametersMap>
> = {
  [CRMChannel.whatsapp]: { name: "whatsappAccount" },
  [CRMChannel.instagram]: { name: "instagramAccount" },
  [CRMChannel.chatbot]: { name: "webChatBotAccount" },
  [CRMChannel.twilio]: { name: "twilioAccount" },
  [CRMChannel.voice]: { name: "voiceAccount" },
  [CRMChannel.email]: { name: "emailAccount" },
};

export const ConversationListSidebar: React.FC = () => {
  const { t } = useTranslations();
  const [selectedFilter, setSelectedFilter] = useState<CRMConversationFilter>(
    CRMConversationFilter.active
  );
  const [selectedMode, setSelectedMode] = useState<CRMConversationMode>(
    CRMConversationMode.allChats
  );
  const { isCx } = useAppVariant();

  const { searchQuery, reset, inputProps } = useSearchInput();
  const { selectedChannel, selectConversation, availableChannels } =
    useClientSupportPageContext();
  const { openManageAccountDrawer } = useManageAccountsDrawer();

  const [isMessageCreditsDialogOpen, setIsMessageCreditsDialogOpen] =
    useState(false);
  const { isOutOfCredits, usage, allowed, renewsDate } = useAIMessageCredits();

  useEffect(() => {
    setSelectedFilter(CRMConversationFilter.active);
    setSelectedMode(CRMConversationMode.allChats);
  }, [selectedChannel]);

  const debouncedSearchQuery = useDebounce(searchQuery) as string;

  const conversationTabs = useMemo(
    () => [
      {
        id: CRMConversationFilter.active,
        label: t({
          id: "client-support-page.active",
          defaultMessage: "Active",
        }),
      },
      {
        id: CRMConversationFilter.expired,
        label: t({
          id: "client-support-page.expired",
          defaultMessage: "Expired",
        }),
      },
      {
        id: CRMConversationFilter.closed,
        label: t({
          id: "client-support-page.closed",
          defaultMessage: "Closed",
        }),
      },
      {
        id: CRMConversationFilter.blocked,
        label: t({
          id: "client-support-page.blocked",
          defaultMessage: "Blocked",
        }),
      },
    ],
    [t]
  );

  const conversationModes: ListBoxItem<{
    icon: React.ReactNode;
    label: string;
  }>[] = useMemo(
    () => [
      {
        id: CRMConversationMode.allChats,
        value: {
          icon: <AllChatsIcon />,
          label: t({
            id: "client-support-page.all-chats",
            defaultMessage: "All chats",
          }),
        },
      },
      {
        id: CRMConversationMode.onAIAutoReply,
        value: {
          icon: <AIIcon />,
          label: t({
            id: "client-support-page.on-ai-auto-reply",
            defaultMessage: "On AI Auto-reply",
          }),
        },
      },
      {
        id: CRMConversationMode.managedByMe,
        value: {
          icon: <SupportIcon />,
          label: t({
            id: "client-support-page.managed-by-me",
            defaultMessage: "Managed by Me",
          }),
        },
      },
      {
        id: CRMConversationMode.waitingResponse,
        value: {
          icon: <InfoIcon />,
          label: t({
            id: "client-support-page.waiting-manual-response",
            defaultMessage: "Awaits manual response",
          }),
        },
      },
    ],
    [t]
  );

  const selectedChannelLabel = useMemo(() => {
    const foundChannel = availableChannels.find(
      (channel) => channel.id === selectedChannel
    );

    if (foundChannel?.name) {
      return foundChannel.name;
    }

    switch (selectedChannel) {
      case CRMChannel.whatsapp:
        return t({
          id: "client-support-page.whatsapp",
          defaultMessage: "WhatsApp",
        });
      case CRMChannel.instagram:
        return t({
          id: "client-support-page.instagram",
          defaultMessage: "Instagram",
        });
      case CRMChannel.chatbot:
        return t({
          id: "client-support-page.web-chat",
          defaultMessage: "Web Chat",
        });
      case CRMChannel.twilio:
        return t({
          id: "client-support-page.twilio",
          defaultMessage: "Text (SMS)",
        });
      case CRMChannel.voice:
        return t({
          id: "client-support-page.voice",
          defaultMessage: "Phone calls",
        });
      case CRMChannel.email:
        return t({
          id: "client-support-page.email",
          defaultMessage: "Email",
        });
      default:
        return t({
          id: "client-support-page.unknown-channel",
          defaultMessage: "Unknown Channel",
        });
    }
  }, [availableChannels, selectedChannel, t]);

  return (
    <div className="flex h-full w-[360px] shrink-0 flex-col border-r border-b border-solid border-dark-100 bg-white max-lg:w-full">
      <div className="flex h-[60px] shrink-0 items-center justify-between gap-3 border-0 border-b border-solid border-dark-100 px-6">
        <span className="truncate font-secondary text-lg font-medium text-dark">
          {selectedChannelLabel}
        </span>
        <div className="flex items-center gap-2">
          {/* AI Agent Insights button (only visible for non-CX workspaces) */}
          {!isCx && (
            <Tooltip
              placement="bottom"
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  ref={ref}
                  {...props}
                  className={cx(collapsedIconButtonClassName, "h-8 w-8")}
                  onClick={() =>
                    openManageAccountDrawer({ name: "aiAgentInsights" })
                  }
                >
                  <InsightsIcon />
                </PlainButton>
              )}
            >
              {t({
                id: "client-support-page.ai-agent-insights",
                defaultMessage: "AI Agent Insights",
              })}
            </Tooltip>
          )}

          {/* Filter conversations button */}
          {selectedFilter === CRMConversationFilter.active &&
            selectedChannel !== CRMChannel.voice && (
              <Popover
                className="w-[300px] p-1.5"
                renderTrigger={({ Trigger, triggerRef, isOpen }) => (
                  <Tooltip
                    placement="bottom"
                    isDisabled={isOpen}
                    renderTrigger={({ props, ref }) => (
                      <div ref={ref} {...props}>
                        <Trigger
                          ref={triggerRef}
                          as={PlainButton}
                          className={cx(
                            collapsedIconButtonClassName,
                            "relative h-8 w-8"
                          )}
                        >
                          <FilterIcon />
                          {selectedMode !== CRMConversationMode.allChats && (
                            <div
                              className="absolute top-[-5px] right-[-5px] flex h-5 w-5 items-center justify-center rounded-full border border-solid border-white bg-tertiary-100 transition-colors hover:bg-tertiary-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMode(CRMConversationMode.allChats);
                              }}
                            >
                              <CloseIcon />
                            </div>
                          )}
                        </Trigger>
                      </div>
                    )}
                  >
                    {t({
                      id: "client-support-page.filter-conversations",
                      defaultMessage: "Filter conversations",
                    })}
                  </Tooltip>
                )}
              >
                {({ onClose }) => (
                  <ListBox
                    className="font-secondary text-[14px]"
                    items={conversationModes}
                    isCompact
                    selectionBehavior={{ canToggle: true, mode: "single" }}
                    renderLabel={({ value }) => value.label}
                    renderEndIcon={({ id }) => (
                      <Radio readOnly isChecked={id === selectedMode} />
                    )}
                    renderStartIcon={({ value }) => value.icon}
                    defaultSelectedIds={[selectedMode]}
                    onSelect={({ id }) => {
                      setSelectedMode(id as CRMConversationMode);
                      onClose();
                    }}
                  />
                )}
              </Popover>
            )}

          {/* Voice channel's out of credits warning */}
          {selectedChannel === CRMChannel.voice && isOutOfCredits && (
            <>
              <Tooltip
                placement="bottom"
                renderTrigger={({ props, ref }) => (
                  <PlainButton
                    ref={ref}
                    {...props}
                    className={cx(collapsedIconButtonClassName, "h-8 w-8")}
                    onClick={() => setIsMessageCreditsDialogOpen(true)}
                  >
                    <WarningIcon />
                  </PlainButton>
                )}
                className="flex flex-col gap-2"
              >
                <span className="font-white text-sm leading-[21px] font-semibold">
                  {t({
                    id: "client-support-page.phone-calls-are-inactive",
                    defaultMessage: "Phone Calls are inactive",
                  })}
                </span>
                <span className="font-white text-sm leading-[21px] font-normal">
                  {t({
                    id: "client-support-page.phone-calls-out-of-credits",
                    defaultMessage:
                      "Workspace is run out of Message Credits. Manage credits to proceed with the Phone Calls automation ℹ️",
                  })}
                </span>
              </Tooltip>
              <MessageCreditsDialog
                isOpen={isMessageCreditsDialogOpen}
                usage={usage}
                allowed={allowed}
                renewsDate={renewsDate}
                onRequestClose={() => setIsMessageCreditsDialogOpen(false)}
              />
            </>
          )}

          {/* Channel settings button */}
          <Tooltip
            placement="bottom"
            renderTrigger={({ props, ref }) => (
              <PlainButton
                ref={ref}
                {...props}
                className={cx(collapsedIconButtonClassName, "h-8 w-8")}
                onClick={() =>
                  openManageAccountDrawer(
                    channelToAccountScreen[selectedChannel]
                  )
                }
              >
                <SettingsIcon />
              </PlainButton>
            )}
          >
            {t({
              id: "client-support-page.channel-settings",
              defaultMessage: "Channel Settings",
            })}
          </Tooltip>
        </div>
      </div>

      <div className="flex flex-col gap-3 p-6 pb-5">
        {selectedChannel !== CRMChannel.voice && (
          <div className="jugl__custom-scrollbar-horizontal grid w-full auto-cols-[minmax(max-content,1fr)] grid-flow-col gap-2 overflow-x-auto pb-1.5">
            {conversationTabs
              .filter((tab) => {
                if (tab.id === CRMConversationFilter.active) {
                  return (
                    isCx ||
                    selectedChannel === CRMChannel.whatsapp ||
                    selectedChannel === CRMChannel.instagram
                  );
                }
                if (tab.id === CRMConversationFilter.closed) {
                  return isCx;
                }
                if (tab.id === CRMConversationFilter.expired) {
                  return (
                    selectedChannel === CRMChannel.whatsapp ||
                    selectedChannel === CRMChannel.instagram
                  );
                }

                if (tab.id === CRMConversationFilter.blocked) {
                  return selectedChannel === CRMChannel.whatsapp;
                }

                return true;
              })
              .map((tab) => (
                <ConversationTab
                  key={tab.id}
                  label={tab.label}
                  isSelected={selectedFilter === tab.id}
                  onSelect={() => {
                    setSelectedFilter(tab.id);
                    selectConversation(undefined);
                  }}
                />
              ))}
          </div>
        )}
        <SearchInput
          variant="filled"
          color="grey"
          {...inputProps}
          onReset={reset}
          onClear={reset}
        />
      </div>
      <div className="jugl__custom-scrollbar flex flex-1 basis-0 flex-col overflow-y-auto pb-px">
        {selectedChannel === CRMChannel.whatsapp ? (
          <WhatsappConversations
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            selectedMode={selectedMode}
            searchQuery={debouncedSearchQuery}
          />
        ) : selectedChannel === CRMChannel.chatbot ? (
          <WebChatBotConversations
            searchQuery={debouncedSearchQuery}
            selectedMode={selectedMode}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
          />
        ) : selectedChannel === CRMChannel.twilio ? (
          <TwilioConversations
            searchQuery={debouncedSearchQuery}
            selectedMode={selectedMode}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
          />
        ) : selectedChannel === CRMChannel.email ? (
          <EmailConversations
            searchQuery={debouncedSearchQuery}
            selectedMode={selectedMode}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
          />
        ) : selectedChannel === CRMChannel.voice ? (
          <VoiceConversations searchQuery={debouncedSearchQuery} />
        ) : (
          <InstagramConversations
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            searchQuery={debouncedSearchQuery}
            selectedMode={selectedMode}
          />
        )}
      </div>
    </div>
  );
};
